// src/components/map/hooks/adapters/useMapHoverLegend.ts
//
// Adapter hook: Hover & Legend
//
// Owns:
//   - onMouseMove() callback (cursor → queryRenderedFeatures → debounced hover)
//   - onMouseLeave() callback (clears hover state)
//   - onMouseMoveDebounce (lodash debounce of updateHoverItemDebounce)
//   - onMouseLeaveDebounce (useDebouncedCallback of onMouseLeave)
//   - updateHoverItemDebounce() (resolves hovered feature → legend item)
//   - dispatchHighlightedFeatures() (dispatches hovered UWIs, or queried facilities)
//   - onHover() effect (hoverLegendItem change → dispatchHighlightedFeatures)
//   - Mouse event registration effect (mousemove + mouseout on mapbox)
//
// Mapbox methods called: queryRenderedFeatures, querySourceFeatures
//
// Jotai writes: hoverLegendBaseAtom (via setHoverLegend)
// Jotai reads: hoverLegendItemAtom, widgetHoverAtom
// Store reads: selectTxnId, setAttentionWells
// Redux reads: groupBy.activeFocus, groupBy.groupByTitle
// Redux writes: setActiveFocus
//
// This hook was extracted from MapboxWrapper as part of the concern-based
// decomposition (Phase 5). The mouse event registration effect was already
// partially split in Phase 4 (click → useMapClickSelection); this phase
// completes the split by moving hover event registration here.
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  EMPTY_HOVER_LEGEND,
  claimHoverOwnership,
  getHoverOwner,
  hoverLegendBaseAtom,
  hoverLegendItemAtom,
  widgetHoverAtom
} from "atoms/interaction";
import {
  FACILITY_LAYER,
  MAX_WELLS_TO_HIGHLIGHT,
  SPIDER_FACILITY_LAYER,
  STICK_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT
} from "constants/mapLayers.constants";
import { useAtomValue, useSetAtom } from "jotai";
import _debounce from "lodash/debounce";
import mapboxgl, { PointLike } from "mapbox-gl";
import { setActiveFocus } from "store/features/groupBy/groupBySlice";
import { RootState } from "store/rootReducer";
import { selectTxnId, useMapStore } from "stores/map";
import { useDebouncedCallback } from "use-debounce";
import { removeCount } from "utils/removeCountFromTitle";
import { removeProbitInfoFromtitle } from "utils/removeProbitInfoFromTitle";

import { EntityKind } from "../../../../models/entityKind";
import { EntitiesInGroupModel } from "../../contexts/MapContextState";
import { waitForStyleToLoad } from "../../utils";
import { findEntitiesFromGroup } from "../../utils/findEntitiesFromGroup";
import { useMapDispatch } from "../useMapDispatch";

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseMapHoverLegendParams {
  /** Ref to the Mapbox GL map instance. */
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
  /** Dashboard widget ID — used to key into the widgetHoverAtom map. */
  readonly id: string;
  /** Ref tracking whether the component is still mounted. */
  readonly isMountedRef: MutableRefObject<boolean>;
  /** Map context dispatch function. */
  readonly mapDispatch: ReturnType<typeof useMapDispatch>;
  /** Ref to the entities-in-group lookup (written by ref-sync effects in shell). */
  readonly entitiesInGroupRef: MutableRefObject<Record<string, EntitiesInGroupModel>>;
  /** Ref to the facilities-in-group lookup (written by ref-sync effects in shell). */
  readonly facilitiesInGroupRef: MutableRefObject<Record<string, EntitiesInGroupModel>>;
  /** Ref tracking polygon-filter visibility (guards mouseleave clearing). */
  readonly isPolygonFilterControlVisibleRef: MutableRefObject<boolean>;
  /** Current lookups, so a held hover re-resolves when a refilter publishes new ones. */
  readonly entitiesInGroup: Record<string, EntitiesInGroupModel>;
  readonly facilitiesInGroup: Record<string, EntitiesInGroupModel>;
}

export interface UseMapHoverLegendReturn {
  /** Raw (non-debounced) mousemove handler, registered on the mapbox instance. */
  readonly onMouseMove: (e: mapboxgl.MapMouseEvent) => void;
  /** Raw (non-debounced) leave handler, registered for mapbox mouseout. */
  readonly onMouseLeave: () => void;
  /** Lodash-debounced version of the internal updateHoverItemDebounce (300 ms). */
  readonly onMouseMoveDebounce: ReturnType<typeof _debounce>;
  /** useDebouncedCallback version of onMouseLeave (50 ms). */
  readonly onMouseLeaveDebounce: ReturnType<typeof useDebouncedCallback>;
  /**
   * Dispatch the hovered `uwis` (UWI → color) to the map context; for a
   * facility hover, the matching facility features instead. Used by the
   * onHover effect internally, but exposed for future hook consumers that need
   * to trigger highlighting programmatically.
   */
  readonly dispatchHighlightedFeatures: (
    uwis: Record<string, string>,
    isFacility?: boolean
  ) => Promise<void>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useMapHoverLegend({
  mapboxRef,
  id,
  isMountedRef,
  mapDispatch,
  entitiesInGroupRef,
  facilitiesInGroupRef,
  isPolygonFilterControlVisibleRef,
  entitiesInGroup,
  facilitiesInGroup
}: UseMapHoverLegendParams): UseMapHoverLegendReturn {
  // ── Redux ──────────────────────────────────────────────────────────────
  const dispatch = useDispatch();
  const activeFocus = useSelector((state: RootState) => state.groupBy.activeFocus);
  const groupByTitle = useSelector((state: RootState) => state.groupBy.groupByTitle);

  // ── Jotai atoms ────────────────────────────────────────────────────────
  const hoverLegendItem = useAtomValue(hoverLegendItemAtom);
  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const isHover = widgetHoverMap?.[id] ?? false;
  const setHoverLegend = useSetAtom(hoverLegendBaseAtom);

  // ── Zustand selectors ──────────────────────────────────────────────────
  const setAttentionWells = useMapStore((s) => s.setAttentionWells);
  const txnId = useMapStore(selectTxnId);

  // ── Map context reads (via refs, to avoid stale closures in callbacks) ─
  // entitiesInGroupRef, facilitiesInGroupRef, isPolygonFilterControlVisibleRef
  // are already passed as params.

  // Lets a slow older hover bail out instead of overwriting a newer hover or clear.
  const hoverSeqRef = useRef(0);

  // ── dispatchHighlightedFeatures ────────────────────────────────────────
  const dispatchHighlightedFeatures = useCallback(
    async (uwis: Record<string, string>, isFacility = false) => {
      const mapbox = mapboxRef?.current as mapboxgl.Map;

      if (!mapbox) {
        return;
      }

      const seq = hoverSeqRef.current;
      const count = Object.keys(uwis).length;
      if (count > MAX_WELLS_TO_HIGHLIGHT || isPolygonFilterControlVisibleRef.current) {
        return;
      }

      // Wells need no query: useMapWellHighlighting filters its layers by `uwis`.
      let facility: GeoJSON.Feature[] = [];
      if (isFacility && count) {
        const isStyleLoaded = await waitForStyleToLoad(mapbox, 100);
        if (seq !== hoverSeqRef.current || !isStyleLoaded) {
          return;
        }
        facility = mapbox.querySourceFeatures(FACILITY_LAYER, {
          sourceLayer: "eva-facility",
          filter: ["has", ["get", "Uwi"], ["literal", uwis]]
        });
      }

      if (isMountedRef.current) {
        mapDispatch({
          payload: {
            highlightedFeatures: {
              line: [],
              point: [],
              facility,
              ...(!isFacility && count > 0 && { uwis })
            }
          }
        });
      }
    },
    [mapDispatch, mapboxRef, isMountedRef, isPolygonFilterControlVisibleRef]
  );

  // ── updateHoverItemDebounce ────────────────────────────────────────────
  const updateHoverItemDebounce = useCallback(
    (feature: GeoJSON.Feature, facilityFeature: GeoJSON.Feature) => {
      const mapbox = mapboxRef.current;
      if (!mapbox || (!feature && !facilityFeature)) {
        return;
      }
      if (feature) {
        const props = feature.properties;
        const uwi: string = props.Uwi;
        setAttentionWells([uwi]);

        let group: string = props.group ?? "";
        for (const groupTitle of Object.keys(entitiesInGroupRef.current)) {
          const foundGroup = entitiesInGroupRef.current[groupTitle];
          if (foundGroup && foundGroup.uwis[uwi]) {
            group = foundGroup.group.title;
            break;
          }
        }

        claimHoverOwnership("map");
        setHoverLegend({
          item: group,
          id: "",
          group: "",
          itemUnfiltered: "",
          isMultiProductChart: false
        });
        dispatch(setActiveFocus(EntityKind.Well));
        return;
      } else if (facilityFeature) {
        const props = facilityFeature.properties;
        const facilityId: string = props.Uwi;
        // needed to highlight the individual facility on dot charts and spaghetti mode
        setAttentionWells([facilityId]);
        let group = "";
        for (const groupTitle of Object.keys(facilitiesInGroupRef.current)) {
          const foundGroup = facilitiesInGroupRef.current[groupTitle];
          if (foundGroup && foundGroup.uwis[facilityId]) {
            group = foundGroup.group.title;
            break;
          }
        }
        claimHoverOwnership("map");
        setHoverLegend({
          item: group,
          id: "",
          group: "",
          itemUnfiltered: "",
          isMultiProductChart: false
        });
        dispatch(setActiveFocus(EntityKind.Facility));
      }
    },
    [
      dispatch,
      setAttentionWells,
      mapboxRef,
      entitiesInGroupRef,
      facilitiesInGroupRef,
      setHoverLegend
    ]
  );

  // ── onMouseMoveDebounce (lodash 300 ms) ────────────────────────────────
  const onMouseMoveDebounce = useMemo(
    () => _debounce(updateHoverItemDebounce, 300),
    [updateHoverItemDebounce]
  );

  // ── onMouseLeave ───────────────────────────────────────────────────────
  const onMouseLeave = useCallback(() => {
    // A hover still pending from the pointer that just left would land after the
    // next surface (e.g. a group-by bin) claimed the hover, and replace it.
    onMouseMoveDebounce.cancel();

    // Don't clear tooltip if widget is being hovered
    if (isHover) {
      return;
    }

    if (!isPolygonFilterControlVisibleRef.current) {
      // The highlight belongs to whoever owns the hover. A legend/group-by hover
      // that landed inside this 50 ms debounce must keep its map highlight.
      const ownsHover = getHoverOwner() === "map";
      // Only clear selected features if polygon filter control is not visible
      if (isMountedRef.current) {
        mapDispatch({
          payload: {
            selectedFeatures: [],
            ...(ownsHover && {
              highlightedFeatures: { line: [], point: [], facility: [] }
            })
          }
        });
      }
      setAttentionWells([]);
      // Only clear the hover atom if the map was the last writer.
      // If a legend item called useHoverLegendWriter (which sets owner to
      // "legend" synchronously on pointerenter), this debounced callback fires
      // 50 ms later and must not overwrite the legend item's hover state.
      if (getHoverOwner() === "map") {
        claimHoverOwnership("none");
        setHoverLegend(EMPTY_HOVER_LEGEND);
      }
    }
  }, [
    dispatch, // eslint-disable-line react-hooks/exhaustive-deps -- kept for parity with original dep array
    setAttentionWells,
    mapDispatch,
    onMouseMoveDebounce,
    isHover,
    isMountedRef,
    isPolygonFilterControlVisibleRef,
    setHoverLegend
  ]);

  // ── onMouseLeaveDebounce (50 ms) ───────────────────────────────────────
  const onMouseLeaveDebounce = useDebouncedCallback(onMouseLeave, 50);

  // ── onMouseMove ────────────────────────────────────────────────────────
  const onMouseMove = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      const mapbox = mapboxRef?.current;
      if (!mapbox) {
        return;
      }

      // Cancel any pending leave operations when mouse is moving (unless widget is hovered)
      if (!isHover) {
        onMouseLeaveDebounce.cancel();
      }

      const buffer = 3;
      const bbox = [
        [e.point.x - buffer, e.point.y - buffer],
        [e.point.x + buffer, e.point.y + buffer]
      ] as [PointLike, PointLike];
      if (!mapbox.getLayer(WELL_LAYER)) {
        return;
      }
      const features = mapbox.queryRenderedFeatures(bbox, {
        layers: [WELL_LAYER, WELL_LAYER_POINT, STICK_LAYER]
      });

      // Build facility layer list dynamically — only include layers that exist
      const facilityLayers = [FACILITY_LAYER, SPIDER_FACILITY_LAYER].filter((id) =>
        mapbox.getLayer(id)
      );

      const facilityFeatures = mapbox.queryRenderedFeatures(bbox, {
        layers: facilityLayers
      });
      const entitiesInGroup = entitiesInGroupRef.current;
      const facilitiesInGroup = facilitiesInGroupRef.current;
      if (
        (!features || !features.length || !entitiesInGroup) &&
        (!facilityFeatures || !facilityFeatures.length || !facilitiesInGroup)
      ) {
        // Only trigger leave if widget is not hovered
        if (!isHover) {
          onMouseLeaveDebounce();
        }
        onMouseMoveDebounce.cancel();
        return;
      }
      onMouseMoveDebounce(features[0], facilityFeatures[0]);
    },
    [
      mapboxRef,
      onMouseLeaveDebounce,
      onMouseMoveDebounce,
      isHover,
      entitiesInGroupRef,
      facilitiesInGroupRef
    ]
  );

  // ── onHover effect (hoverLegendItem → dispatchHighlightedFeatures) ─────
  useEffect(() => {
    function onHover(
      item: string,
      currentActiveFocus: EntityKind,
      currentGroupByTitle: string
    ) {
      if (!mapboxRef.current) {
        return;
      }

      ++hoverSeqRef.current;

      if (!item) {
        dispatchHighlightedFeatures({});
        return;
      }

      const itemTitle = removeCount(removeProbitInfoFromtitle(item));
      const groupKeys = Object.keys(entitiesInGroup);
      const index = groupKeys.findIndex((f) => removeCount(f) === itemTitle);

      let entities: Maybe<EntitiesInGroupModel>;
      const isFacility =
        currentGroupByTitle?.includes(EntityKind.Facility) ||
        currentActiveFocus === EntityKind.Facility;

      if (isFacility) {
        entities =
          facilitiesInGroup[item] ||
          (index >= 0 ? facilitiesInGroup[groupKeys[index]] : undefined);
      } else {
        entities =
          entitiesInGroup[item] ||
          (index >= 0 ? entitiesInGroup[groupKeys[index]] : undefined);
      }

      if (!entities) {
        const uwi = itemTitle.replaceAll("/", "").replaceAll("-", "");
        const items = findEntitiesFromGroup([uwi], entitiesInGroup);
        if (items && uwi in items) {
          entities = {
            uwis: { [uwi]: items[uwi].color },
            group: {
              id: 0,
              title: items[uwi].group,
              color: items[uwi].color
            }
          } as unknown as EntitiesInGroupModel;
        }

        // Clear, not keep: after a refilter that drops the hovered group, the
        // last dispatch still holds its old (now filtered-out) wells.
        if (!entities) {
          dispatchHighlightedFeatures({});
          return;
        }
      }
      dispatchHighlightedFeatures(entities.uwis, isFacility);
    }

    onHover(hoverLegendItem, activeFocus, groupByTitle);
  }, [
    hoverLegendItem,
    // A held hover re-resolves against the lookups a refilter publishes after its txn.
    entitiesInGroup,
    facilitiesInGroup,
    mapboxRef,
    groupByTitle,
    activeFocus,
    dispatchHighlightedFeatures,
    // MapboxWrapper clears highlightedFeatures on every new txn; re-dispatch the
    // unchanged hover after it (this hook's effects run later in the same commit).
    txnId
  ]);

  // ── Mouse event registration ───────────────────────────────────────────
  // Click event registration lives in useMapClickSelection (Phase 4).
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox) {
      return;
    }

    mapbox.on("mousemove", onMouseMove);
    // No layer mouseleave: it fires once the cursor is off a feature's exact pixels,
    // which cancelled hovers onMouseMove had scheduled within its 3 px buffer. Inside
    // the canvas onMouseMove leaves with that same buffer; this covers leaving the canvas.
    mapbox.on("mouseout", onMouseLeave);

    return () => {
      mapbox.off("mousemove", onMouseMove);
      mapbox.off("mouseout", onMouseLeave);
      // Cancel any pending operations on cleanup
      onMouseMoveDebounce.cancel();
      onMouseLeaveDebounce.cancel();
    };
  }, [
    mapboxRef?.current, // eslint-disable-line react-hooks/exhaustive-deps -- re-register when map instance changes
    onMouseLeave,
    onMouseMove,
    onMouseMoveDebounce,
    onMouseLeaveDebounce
  ]);

  return {
    onMouseMove,
    onMouseLeave,
    onMouseMoveDebounce,
    onMouseLeaveDebounce,
    dispatchHighlightedFeatures
  };
}
